import { memo, useCallback, useMemo } from 'react';
import { cn, Discount, Gallery, PopoverInfo, Price } from '@divlab/divanui';
import { IconInfoPopup } from '@divlab/divanui/icons';

import * as ModalWindows from '@Stores/ModalWindows';
import * as Filtrator from '@Stores/Filtrator';
import useTranslation from '@Queries/useTranslation';
import useMedias from '@Hooks/useMedias';
import Link from '@UI/Link';
import Collection from '../../../Collection';
import ButtonTab from '../../../ButtonTab';
import OptionCard from '../../../OptionCard';
import SchemeCard from '../../../SchemeCard';
import MixedOutput from '../../../MixedOutput';
import styles from './ConfiguratorTabContent.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type {
  ConfiguratorOption,
  ConfiguratorOptionItem,
  ConfiguratorOptionItemFabric,
  ConfiguratorOptionItemParameter,
  ConfiguratorOptionItemProduct,
  ConfiguratorOptionItemSize,
  ConfiguratorTab as ConfiguratorTabData,
} from '@Pages/PageProduct/typings';
import type { ChangeConfigurationProps } from '../../../../Sidebar';

export interface ConfiguratorTabProps extends HTMLAttributes<HTMLDivElement> {
  tab: ConfiguratorTabData;
  entityId: Filtrator.FiltratorId;
  onClickCollection?: (collection: ConfiguratorOption) => void;
  onClickItem?: (e: MouseEvent, item: ChangeConfigurationProps) => void;
}

const Container: FC<HTMLAttributes<HTMLDivElement>> = (props) => {
  const { className, ...restProps } = props;
  const { isMobile } = useMedias();
  return isMobile ? (
    <Gallery spaceBetween={10} cnSlide={styles.optionSile} {...restProps} />
  ) : (
    <div className={className} {...restProps} />
  );
};

const ConfiguratorTabContent = (props: ConfiguratorTabProps) => {
  const { tab, entityId, onClickCollection, onClickItem, ...restProps } = props;
  const { t } = useTranslation();
  const appliedFilters = Filtrator.useAppliedValues(entityId);

  // Группируем образцы по коллекциям если не применен ни один фильтр, либо включен переключатель Есть на складе
  const isGroupingByCollections = useMemo(() => {
    const inStock = appliedFilters.some((filter) => filter.parameterId === 'in_stock');

    return !appliedFilters?.length || inStock;
  }, [appliedFilters]);

  const handleClickitem = useCallback(
    (e: MouseEvent, item: ConfiguratorOptionItem, parameterId: number, overwrite?: boolean) => {
      if (!onClickItem) return;

      onClickItem(e, { item: { tab, item, parameterId }, overwrite });
    },
    [tab, onClickItem],
  );

  const handleClickInfo = useCallback((option: ConfiguratorOption) => {
    if (!option) return;

    ModalWindows.open('CollectionInfo', { collection: { ...option, name: option.title } });
  }, []);

  const renderContent = useCallback(
    (option: ConfiguratorOption) => {
      switch (option.theme) {
        case 'additional': {
          return (
            <div
              className={cn(styles.grid, { [styles.wide]: true })}
              key={option.id || option.title}
            >
              {option.items.map((val: ConfiguratorOptionItemProduct) => (
                <OptionCard
                  key={`${val.id}_${val.count}`}
                  item={{ ...val, type: 'product' }}
                  needCounter
                  onClickItem={(e, constructorValue, overwrite) =>
                    handleClickitem(e, constructorValue, option.parameterId, overwrite)
                  }
                />
              ))}
            </div>
          );
        }

        case 'tabs': {
          return (
            <div key={option.title} className={styles.list}>
              {option.items.map((optionTab: ConfiguratorOptionItemSize) => (
                <ButtonTab
                  key={`${optionTab.id}_${optionTab.title}`}
                  item={{ ...optionTab, disabled: optionTab.calcDisabled || optionTab.disabled }}
                  tabName={tab.title}
                  onClickItem={(e) => {
                    handleClickitem(e, optionTab, option.parameterId);
                  }}
                />
              ))}
            </div>
          );
        }

        case 'image': {
          return (
            <Container className={cn(styles.grid, { [styles.middle]: true })} key={option.title}>
              {option.items.map(
                (val: ConfiguratorOptionItemFabric | ConfiguratorOptionItemProduct) => (
                  <OptionCard
                    key={val.id}
                    item={val}
                    onClickItem={(e, constructorValue) =>
                      handleClickitem(e, constructorValue, option.parameterId)
                    }
                  />
                ),
              )}
            </Container>
          );
        }

        case 'parameter': {
          return (
            <div className={styles.grid} key={option.title}>
              {option.items.map((val: ConfiguratorOptionItemParameter) => (
                <OptionCard
                  key={val.id}
                  item={val}
                  orientation='landscape'
                  onClickItem={(e, constructorValue) => {
                    handleClickitem(e, constructorValue, option.parameterId);
                  }}
                />
              ))}
            </div>
          );
        }

        case 'scheme': {
          return (
            <div className={cn(styles.grid, { [styles.wide]: true })} key={option.title}>
              {option.items.map((val: ConfiguratorOptionItemParameter) => (
                <SchemeCard
                  key={val.id}
                  item={val}
                  onClickItem={(e, constructorValue) =>
                    handleClickitem(e, constructorValue, option.parameterId)
                  }
                />
              ))}
            </div>
          );
        }

        case 'color': {
          return (
            <div key={option.id || option.title}>
              <Collection
                className={styles.collection}
                collection={option}
                tabName={tab.title}
                onClickItem={(e, constructorValue) =>
                  handleClickitem(e, constructorValue, option.parameterId)
                }
                onClickCollection={onClickCollection}
              />
            </div>
          );
        }

        default: {
          return null;
        }
      }
    },
    [handleClickitem, onClickCollection, tab],
  );

  const renderMixedContent = useCallback(
    (list: ConfiguratorTabData) => {
      const { parameterId } = list.options[0] || {};

      return (
        <MixedOutput
          className={styles.collection}
          options={list.options}
          tabName={tab.title}
          onClickItem={(e, value) => handleClickitem(e, value, parameterId)}
          onClickCollection={onClickCollection}
        />
      );
    },
    [handleClickitem, onClickCollection, tab.title],
  );
  return (
    <div {...restProps}>
      {!!tab.warnings &&
        tab.warnings.map((warning) => (
          <div key={warning} className={styles.attention}>
            {warning}
          </div>
        ))}

      {isGroupingByCollections || tab.id !== 'upholstery'
        ? tab.options.map((option) => {
            const hasDescription = !!option.description && option.images?.length > 0;
            const hasExplain = !!option.description && !option.images?.length;
            const hasInfo = !!option.info;
            const isOutlet = option.id === 'outlet';
            const isBase = option.id === 'baseCollection';

            const showWarn =
              tab.warnings?.length > 0 && option.items.some((item) => item.calcDisabled);
            const isSamePrice = option.items.every(
              (fabric: ConfiguratorOptionItemFabric) =>
                fabric.price.actual === option.minPrice?.actual,
            );

            const needShowFabricPrice =
              (isBase || isOutlet || isSamePrice) && option.minPrice?.actual > 0;
            const unit = (option.items[0] as ConfiguratorOptionItemSize).unit;
            const countActiveOptions = tab.options.filter((opt) => !opt.hidden).length;

            return (
              <div
                key={option.title}
                className={cn(styles.option, {
                  [styles.special]:
                    (tab.id !== 'upholstery' || option.id === 'outlet') &&
                    option.theme === 'color' &&
                    countActiveOptions > 1,
                  [styles.hidden]: option.hidden,
                })}
              >
                {option.title && (
                  <div className={styles.header}>
                    <div
                      className={cn(styles.title, { [styles.hasAction]: hasDescription })}
                      onClick={() => hasDescription && handleClickInfo(option)}
                    >
                      <Link
                        className={cn(styles.name, { [styles.active]: hasInfo })}
                        view='primary'
                        underlined={hasDescription}
                      >
                        {`${option.title}${unit ? `, ${unit}` : ''}`}
                      </Link>
                      {hasDescription && <IconInfoPopup />}

                      {hasInfo && !hasDescription && (
                        <PopoverInfo
                          preferredPositions={['bottom-right']}
                          slotTrigger={<IconInfoPopup className={styles.icon} />}
                        >
                          <div className={styles.popover}>{option.info}</div>
                        </PopoverInfo>
                      )}
                    </div>

                    {needShowFabricPrice && option.minPrice.actual > 0 && (
                      <div className={styles.price}>
                        {isSamePrice ? '' : `${t('ui.range.from')} `}

                        <div className={styles.priceValue}>
                          <Price price={option.minPrice.actual} className={styles.totalPrice} />

                          {option.minPrice.expired > 0 && (
                            <Price
                              expired
                              price={option.minPrice.expired}
                              className={styles.priceExpired}
                            />
                          )}
                          {option.minPrice.discount > 0 && (
                            <Discount
                              className={styles.discount}
                              value={option.minPrice.discount}
                              simple
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {showWarn &&
                  tab.warnings.map((warning) => (
                    <div key={warning} className={styles.warning}>
                      {warning}
                    </div>
                  ))}
                {hasExplain && <div className={styles.description}>{option.description}</div>}
                {renderContent(option)}
              </div>
            );
          })
        : renderMixedContent(tab)}
    </div>
  );
};

export default memo(ConfiguratorTabContent);
